import { Flex, Form } from "antd";
import { MergeFlowModalContent } from "./MergeFlow/MergeFlowModalContent";
import { PatientSearchTab } from "./SearchFlow/PatientSearchTab";
import { PaymentFooter } from "./PaymentRequest/PaymentFooter";
import { PaymentForm } from "./PaymentForm/PaymentForm";
import {
    SearchFlowContext,
    type SelectedInsuranceCarrier,
    type SelectedPatient,
    useSearchFlowContext,
} from "./SearchFlow/useSearchFlow";
import { SearchFlowContextProvider } from "./SearchFlow/SearchFlowContextProvider";
import { TransactorKind } from "@vyne-shared/components/Payment/transactorKind";
import { UnmergeButton } from "./PaymentRequest/UnmergeButton";
import { create } from "@ebay/nice-modal-react";
import type { PatientUpdateForm } from "api/requests";

/**
 * Modal for creating a new payment request. This includes creating new users if necessary.
 * Also, in PMS-integrated sessions, provides the ability to merge an unlinked P/RP with a PMS patient.
 * Note that this is *not* take-a-payment.
 *
 * NiceModal renders these in the larger DOM scope, which allows them to be open/closed from anywhere within the app.
 * This also decouples the Modal from the calling control, which means React Context cannot be used.
 */
export const NewPaymentModal = create(() => {
    // note that when existingPrp.id is not defined, existingPrp represents a VyneSync patient (and not an actual P/RP)
    return (
        <SearchFlowContextProvider>
            <SearchFlowContext.Consumer>
                {() => {
                    const { merging } = useSearchFlowContext();
                    return merging ? <MergeFlowModalContent /> : <NewPaymentModalContent />;
                }}
            </SearchFlowContext.Consumer>
        </SearchFlowContextProvider>
    );
});

const NewPaymentModalContent = () => {
    const [paymentForm] = Form.useForm<PatientUpdateForm>();
    const { merging, modal, selectedTransactor } = useSearchFlowContext();

    if (merging) {
        throw new Error("NewPaymentModalContent should not be used in a merging state");
    }

    const existingPrp = getExistingPrp(selectedTransactor);

    return (
        <PatientSearchTab
            title="New Payment Request"
            footer={<PaymentFooter hostModal={modal} paymentForm={paymentForm} existingPrp={existingPrp} />}
        >
            <PaymentForm form={paymentForm} existingPrp={existingPrp} />
            <Flex align="center" justify="center">
                <UnmergeButton modal={modal} existingPrp={existingPrp} />
            </Flex>
        </PatientSearchTab>
    );
};

function getExistingPrp(selectedTransactor: SelectedPatient | SelectedInsuranceCarrier | undefined) {
    if (!selectedTransactor) {
        return undefined;
    }

    if (selectedTransactor.kind !== TransactorKind.Patient) {
        throw new Error("NewPaymentModalContent should not be used with a non-patient (insurance carrier) selected");
    }

    return selectedTransactor.transactor;
}
